import React from "react";
import {CommunicationError, ErrorPage, NotAuthorized} from "../common/errors";
import {LibrarySimpleDTO} from "../../models/library";
import {useKeycloak} from "@react-keycloak/web";
import {API, createApiConfig} from "../../utils/API";
import {AxiosError} from "axios";
import {LoadingBackdrop} from "../common/Loading";
import {LibrariesList} from "./list/LibrariesList";
import {Box, Container} from "@material-ui/core";
import {PageHeader, PrimaryPageAction} from "../common/headers";
import AddIcon from "@material-ui/icons/Add";
import ROUTES from "../../routes/routes";
import {BreadcrumbItem, BreadcrumbsRow} from "../common/breadcrumbs";
import {EditLinkActionButton, EditorLinkActionButton, LinkActionButton, VisitLinkActionButton} from "../common/buttons";
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import {Link} from "react-router-dom";
import SettingsIcon from "@material-ui/icons/Settings";
import {useTranslation} from "react-i18next";
import EmptySVG from "../../graphics/undraw/empty_xct9.svg";
import {LibraryDialog} from "../values/dialogs/LibraryDialog";

const LibrariesEmpty: React.FC = () => {
    const [t] = useTranslation("libraries");
    return (
        <ErrorPage
            title={t("library.empty.admin.title")}
            description={t("library.empty.admin.description")}
            image={EmptySVG}
        />
    );
};

interface LibrariesAdminItemProps {
    library: LibrarySimpleDTO;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        cardLink: {
            textDecoration: "none",
            display: "block",
            height: "100%",
        },
        libraryCard: {
            backgroundColor: "#ffffff",
            borderRadius: "10px",
            border: "1px solid #E5E7EB",
            padding: theme.spacing(2),
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(0.5),
            height: "100%",
            boxSizing: "border-box" as const,
            position: "relative" as const,
            transition: "border-color 0.15s, box-shadow 0.15s",
            "&:hover": {
                borderColor: "#2563EB",
                boxShadow: "0 2px 8px rgba(37,99,235,0.1)",
            },
        },
        libraryVersion: {
            position: "absolute" as const,
            top: theme.spacing(2),
            right: theme.spacing(2),
            fontSize: "0.75rem",
            color: "#6B7280",
            fontWeight: 500,
        },
        libraryTitle: {
            fontSize: "0.9375rem",
            fontWeight: 700,
            color: "#111827",
            paddingRight: "48px",
        },
        libraryOrg: {
            fontSize: "0.8125rem",
            color: "#6B7280",
        },
        libraryMeta: {
            fontSize: "0.8125rem",
            color: "#6B7280",
        },
        cardActions: {
            marginTop: theme.spacing(1),
            paddingTop: theme.spacing(1),
            borderTop: "1px solid #F3F4F6",
            display: "flex",
            gap: "2px",
        },
    })
);

const LibrariesAdminItem: React.FC<LibrariesAdminItemProps> = (props: LibrariesAdminItemProps) => {
    const classes = useStyles();
    const [t] = useTranslation("libraries");
    const [infoOpen, setInfoOpen] = React.useState(false);
    return (
        <Box className={classes.libraryCard}>
            <LibraryDialog library={props.library} open={infoOpen} handleClose={() => setInfoOpen(false)} />
            <span className={classes.libraryVersion}>{props.library.version}</span>
            <Link to={ROUTES.library.create(props.library.id)} className={classes.cardLink} style={{flexGrow: 1}}>
                <Box className={classes.libraryTitle}>{props.library.name}</Box>
                <Box className={classes.libraryOrg}>{props.library.organization.name}</Box>
                {props.library.license && <Box className={classes.libraryMeta}>License: {props.library.license}</Box>}
            </Link>
            <Box className={classes.cardActions}>
                <VisitLinkActionButton to={ROUTES.library.create(props.library.id)} />
                <EditorLinkActionButton to={ROUTES.libraryEditor.create(props.library.id)} />
                <EditLinkActionButton to={ROUTES.libraryEdit.create(props.library.id)} />
                <LinkActionButton
                    title={t("library.actions.manage")}
                    icon={<SettingsIcon />}
                    to={ROUTES.libraryAdmin.create(props.library.id)}
                />
            </Box>
        </Box>
    );
};

interface LibrariesAdminState {
    libraries: LibrarySimpleDTO[];
    name: "loading" | "loaded" | "failed" | "not_found" | "unauthorized";
}

export const LibrariesAdmin: React.FC = () => {
    const [t] = useTranslation("libraries");
    const {keycloak, initialized} = useKeycloak();
    const initState: LibrariesAdminState = {
        libraries: [],
        name: "loading",
    };
    const [state, setState] = React.useState(initState);
    React.useEffect(() => {
        if (state.name === "loading") {
            API.get<LibrarySimpleDTO[]>(`/libraries/all`, createApiConfig(keycloak, initialized))
                .then((res) => {
                    setState({...state, libraries: res.data, name: "loaded"});
                })
                .catch((err: AxiosError) => {
                    if (err?.response?.status === 403 || err?.response?.status === 401) {
                        setState({...state, name: "unauthorized"});
                    } else {
                        setState({...state, name: "failed"});
                    }
                });
        }
    }, [state, setState, keycloak, initialized]);
    if (state.name === "loading") {
        return <LoadingBackdrop />;
    }
    if (state.name === "unauthorized") {
        return <NotAuthorized />;
    }
    if (state.name === "failed") {
        return <CommunicationError />;
    }
    return (
        <Container>
            <BreadcrumbsRow>
                <BreadcrumbItem name={t("library.actions.admin")} />
                <BreadcrumbItem name={t("library.titles.libraries")} />
            </BreadcrumbsRow>
            <PageHeader title={t("library.titles.manage_libraries")}>
                <PrimaryPageAction title={t("library.titles.new_library")} to={ROUTES.libraryNewAdmin.path}>
                    <AddIcon />
                </PrimaryPageAction>
            </PageHeader>
            {state.libraries.length > 0 && (
                <LibrariesList libraries={state.libraries}>
                    {(lib) => <LibrariesAdminItem key={lib.id} library={lib} />}
                </LibrariesList>
            )}
            {state.libraries.length === 0 && <LibrariesEmpty />}
        </Container>
    );
};
